import React, { useEffect, useRef, useState, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize, Scan } from "lucide-react";
import { Button } from "@/components/ui/button";

// Worker served from /public — avoids CDN CORS issues AND avoids the
// `new URL(..., import.meta.url)` asset-resolution pattern which CRA's
// production build (webpack5 + Terser) does not reliably support, causing
// "Failed to load PDF file" only in the deployed build (works in dev).
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 5;
const ZOOM_STEP = 0.25;

/**
 * Renders a plan file (image or multi-page PDF) in a full-width, scrollable viewport.
 * Zoom 1 = "fit to width" (the plan always fills the available width without overflowing).
 * Zooming in enlarges beyond the viewport with scroll; Ctrl+molette also zooms.
 * `containerRef` points at the element that has the SAME bounding box as the rendered
 * image/canvas — clicks and marker overlays share this coord space.
 */
export default function PlanRenderer({ url, alt, onClick, onPageChange, containerRef, className = "", children }) {
  const isPdf = url?.toLowerCase().endsWith(".pdf");
  const isProtected = url?.startsWith("/api/files/");
  const [blob, setBlob] = useState(null);
  const [numPages, setNumPages] = useState(1);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [wrapWidth, setWrapWidth] = useState(0);
  const wrapRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => { onPageChange && onPageChange(page); }, [page, onPageChange]);

  // Measure available width (fit-to-width baseline)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWrapWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!isProtected) return;
    let current = null;
    const token = localStorage.getItem("wrm_token");
    const full = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    fetch(full, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => { current = URL.createObjectURL(b); setBlob(current); })
      .catch(() => {});
    return () => current && URL.revokeObjectURL(current);
  }, [url, isProtected]);

  const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
  const zoomIn = useCallback(() => setZoom((z) => clampZoom(Math.round((z + ZOOM_STEP) * 100) / 100)), []);
  const zoomOut = useCallback(() => setZoom((z) => clampZoom(Math.round((z - ZOOM_STEP) * 100) / 100)), []);
  const fitWidth = useCallback(() => {
    setZoom(1);
    if (scrollRef.current) { scrollRef.current.scrollLeft = 0; scrollRef.current.scrollTop = 0; }
  }, []);

  // Ctrl + wheel zoom (needs a non-passive listener to preventDefault)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => clampZoom(Math.round((z + (e.deltaY < 0 ? 0.1 : -0.1)) * 100) / 100));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const src = isProtected ? blob : url;
  const renderWidth = wrapWidth > 0 ? Math.max(50, wrapWidth * zoom) : undefined;

  // ---- Mini-carte de navigation (visible quand le plan dépasse la zone visible) ----
  const miniRef = useRef(null);
  const miniDrag = useRef(false);
  const [scrollInfo, setScrollInfo] = useState(null);

  const updateScrollInfo = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollInfo({ sl: el.scrollLeft, st: el.scrollTop, cw: el.clientWidth, ch: el.clientHeight, sw: el.scrollWidth, sh: el.scrollHeight });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollInfo, { passive: true });
    return () => el.removeEventListener("scroll", updateScrollInfo);
  }, [updateScrollInfo]);

  useEffect(() => {
    const t = setTimeout(updateScrollInfo, 350);
    return () => clearTimeout(t);
  }, [zoom, wrapWidth, page, src, updateScrollInfo]);

  const scrollFromMini = useCallback((clientX, clientY) => {
    const el = scrollRef.current;
    const mini = miniRef.current;
    if (!el || !mini) return;
    const r = mini.getBoundingClientRect();
    const px = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    const py = Math.min(1, Math.max(0, (clientY - r.top) / r.height));
    el.scrollLeft = px * el.scrollWidth - el.clientWidth / 2;
    el.scrollTop = py * el.scrollHeight - el.clientHeight / 2;
  }, []);

  const showMini = scrollInfo && (scrollInfo.sw - scrollInfo.cw > 8 || scrollInfo.sh - scrollInfo.ch > 8);
  let minimap = null;
  if (showMini && src) {
    const mScale = Math.min(150 / scrollInfo.sw, 190 / scrollInfo.sh);
    const mw = Math.round(scrollInfo.sw * mScale);
    const mh = Math.round(scrollInfo.sh * mScale);
    minimap = (
      <div
        ref={miniRef}
        data-testid="plan-minimap"
        className="absolute bottom-3 right-3 z-10 rounded-md border border-slate-300 bg-white shadow-lg overflow-hidden cursor-pointer select-none touch-none"
        style={{ width: mw, height: mh }}
        onPointerDown={(e) => { e.preventDefault(); miniDrag.current = true; scrollFromMini(e.clientX, e.clientY); try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ } }}
        onPointerMove={(e) => { if (miniDrag.current) scrollFromMini(e.clientX, e.clientY); }}
        onPointerUp={(e) => { miniDrag.current = false; try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ } }}
        title="Mini-carte — cliquez ou glissez pour naviguer"
      >
        {isPdf ? (
          <Document file={src} loading={null}>
            <Page pageNumber={page} width={mw} renderTextLayer={false} renderAnnotationLayer={false} />
          </Document>
        ) : (
          <img src={src} alt="" className="w-full h-full object-contain pointer-events-none" draggable={false} />
        )}
        <div
          data-testid="plan-minimap-viewport"
          className="absolute border-2 border-blue-500 bg-blue-500/10 rounded-sm pointer-events-none"
          style={{ left: scrollInfo.sl * mScale, top: scrollInfo.st * mScale, width: scrollInfo.cw * mScale, height: scrollInfo.ch * mScale }}
        />
      </div>
    );
  }

  const toolbar = (
    <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="pdf-toolbar">
      {isPdf && (
        <>
          <Button size="sm" variant="outline" onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} data-testid="pdf-prev">
            <ChevronLeft size={14} />
          </Button>
          <div className="text-xs font-mono px-2 py-1 rounded bg-slate-100" data-testid="pdf-page-num">Page {page} / {numPages}</div>
          <Button size="sm" variant="outline" onClick={() => setPage(Math.min(numPages, page + 1))} disabled={page >= numPages} data-testid="pdf-next">
            <ChevronRight size={14} />
          </Button>
          <div className="w-px h-6 bg-slate-200 mx-1" />
        </>
      )}
      <Button size="sm" variant="outline" onClick={zoomOut} disabled={zoom <= ZOOM_MIN} data-testid="pdf-zoom-out" title="Dézoomer"><ZoomOut size={14} /></Button>
      <div className="text-xs font-mono px-2 py-1 rounded bg-slate-100 min-w-[52px] text-center" data-testid="pdf-zoom-level">{Math.round(zoom * 100)}%</div>
      <Button size="sm" variant="outline" onClick={zoomIn} disabled={zoom >= ZOOM_MAX} data-testid="pdf-zoom-in" title="Zoomer"><ZoomIn size={14} /></Button>
      <Button size="sm" variant="outline" onClick={fitWidth} data-testid="pdf-fit-width" title="Ajuster à la largeur"><Scan size={14} className="mr-1" /> Ajuster</Button>
      <Button size="sm" variant="outline" onClick={() => setZoom(2)} data-testid="pdf-zoom-200" title="Zoom 200%"><Maximize size={14} className="mr-1" /> 200%</Button>
      <span className="text-[10px] text-slate-400 hidden md:inline">Ctrl + molette pour zoomer</span>
    </div>
  );

  const scrollBox = (inner) => (
    <div className="relative w-full">
      <div ref={scrollRef} className="w-full overflow-auto bg-slate-100 rounded-lg max-h-[75vh]" style={{ overscrollBehavior: "contain" }} data-testid="plan-scroll-area">
        {inner}
      </div>
      {minimap}
    </div>
  );

  if (isPdf) {
    return (
      <div ref={wrapRef} className={`relative w-full ${className}`}>
        {toolbar}
        {scrollBox(
          src ? (
            <div ref={containerRef} onClick={onClick} className="relative inline-block" data-plan-page={page}>
              <Document file={src} onLoadSuccess={({ numPages: n }) => setNumPages(n)} loading={<div className="p-10 text-slate-400 text-sm">Chargement du PDF…</div>}>
                <Page pageNumber={page} width={renderWidth} renderTextLayer={false} renderAnnotationLayer={false} />
              </Document>
              {children && <div className="absolute inset-0 pointer-events-none">{children}</div>}
            </div>
          ) : (
            <div className="w-full h-[500px] bg-slate-100 animate-pulse" />
          )
        )}
      </div>
    );
  }

  return (
    <div ref={wrapRef} className={`relative w-full ${className}`}>
      {toolbar}
      {scrollBox(
        <div ref={containerRef} onClick={onClick} className="relative inline-block" data-plan-page="1" style={renderWidth ? { width: renderWidth } : undefined}>
          <img src={src || url} alt={alt} className="block w-full h-auto" draggable={false} />
          {children && <div className="absolute inset-0 pointer-events-none">{children}</div>}
        </div>
      )}
    </div>
  );
}
